import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Clock, 
  Calendar, 
  ExternalLink,
  AlertCircle,
  CheckCircle,
  XCircle,
  FileText
} from 'lucide-react';
import { useTimesheetForScreen } from '@/contexts/TimesheetContext';
import { useDataSource } from '@/contexts/DataSourceContext';
import { useDashboardSummaryContext } from '@/contexts/DashboardSummaryContext';
import { format, startOfWeek, addDays } from 'date-fns';
import type { TimesheetStatus } from '@/types/timesheet';
import { useOrganisationSettings } from '@/hooks/useOrganisationSettings';
import { useSettings } from '@/contexts/SettingsContext';

interface ThisWeekCardProps {
  onNavigateToTimesheet?: () => void;
}

export function ThisWeekCard({ onNavigateToTimesheet }: ThisWeekCardProps) {
  const { timesheets, currentEmployee } = useTimesheetForScreen('homepage');
  const { settings } = useOrganisationSettings('homepage');
  const { weekStartDay } = useSettings();
  const { getScreenMode } = useDataSource();
  const { summary } = useDashboardSummaryContext();
  const mode = getScreenMode('homepage');

  // Don't render until settings are loaded from the API
  // This ensures we never show default values before the actual API data
  if (!settings) {
    return null;
  }

  const weekData = useMemo(() => {
    // Use organization's configured week start day from Settings context
    // weekStartDay is 0-6 (0=Sunday, 1=Monday, etc.) matching date-fns weekStartsOn
    const currentWeek = startOfWeek(new Date(), { weekStartsOn: weekStartDay as 0 | 1 | 2 | 3 | 4 | 5 | 6 });
    const currentWeekEnd = addDays(currentWeek, 6);
    const today = new Date();

    // Use backend data in live mode when available
    if (mode === 'live' && summary?.thisWeek) {
      const hoursLogged = Number(summary.thisWeek.totalHours);
      const expectedHours = Number(summary.thisWeek.targetHours);
      const progressPercentage = expectedHours > 0 ? Math.min((hoursLogged / expectedHours) * 100, 100) : 0;
      const status: TimesheetStatus = (summary.thisWeek.status as TimesheetStatus) || 'draft';
      const dueDate = new Date(summary.thisWeek.deadline);
      const daysUntilDue = summary.thisWeek.daysUntilDeadline;
      const isOverdue = daysUntilDue < 0 && status === 'draft';
      const isDueSoon = daysUntilDue <= 1 && daysUntilDue >= 0 && status === 'draft';

      // Missing entries still need client-side calculation from timesheet data
      const weekStartStr = format(currentWeek, 'yyyy-MM-dd');
      const weekEndStr = format(currentWeekEnd, 'yyyy-MM-dd');
      const currentTimesheet = timesheets.find(
        ts => ts.employeeId === currentEmployee.id &&
        format(ts.weekEnding, 'yyyy-MM-dd') >= weekStartStr &&
        format(ts.weekEnding, 'yyyy-MM-dd') <= weekEndStr
      );
      const dayOfWeek = today.getDay();
      const workdaysPassed = dayOfWeek === 0 ? 5 : Math.min(dayOfWeek, 5);
      const daysWithEntries = new Set<string>();
      currentTimesheet?.entries.forEach(entry => {
        Object.entries(entry.hours).forEach(([day, hours]) => {
          if (hours > 0) daysWithEntries.add(day);
        });
      });
      const dayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
      const expectedDays: string[] = [];
      for (let i = 0; i < workdaysPassed; i++) {
        expectedDays.push(dayNames[i]);
      }
      const missingDays = expectedDays.filter(day => !daysWithEntries.has(day));
      const hasMissingEntries = missingDays.length > 0 && status === 'draft';
      const shouldShowMissingAlert = hasMissingEntries && (dayOfWeek === 4 || dayOfWeek === 5);

      return {
        weekStart: currentWeek,
        weekEnd: currentWeekEnd,
        hoursLogged,
        expectedHours,
        progressPercentage,
        status,
        dueDate,
        daysUntilDue,
        isOverdue,
        isDueSoon,
        hasTimesheet: !!currentTimesheet,
        missingDays,
        hasMissingEntries,
        shouldShowMissingAlert,
        canAddTime: status === 'draft' || status === 'rejected' || status === 'recalled'
      };
    }

    // Fallback to client-side calculation for demo mode
    // Find current week timesheet
    const weekStartStr = format(currentWeek, 'yyyy-MM-dd');
    const weekEndStr = format(currentWeekEnd, 'yyyy-MM-dd');
    const currentTimesheet = timesheets.find(
      ts => ts.employeeId === currentEmployee.id &&
      format(ts.weekEnding, 'yyyy-MM-dd') >= weekStartStr &&
      format(ts.weekEnding, 'yyyy-MM-dd') <= weekEndStr
    );

    // Calculate hours logged
    const hoursLogged = currentTimesheet?.entries.reduce((sum, entry) => {
      return sum + Object.values(entry.hours).reduce((h, v) => h + v, 0);
    }, 0) || 0;

    // Get expected hours: prefer employee's own working pattern, fall back to org settings
    const expectedHours = currentEmployee?.workingPattern?.hoursPerWeek
      ?? settings.workingProfile?.standardHoursPerWeek
      ?? 40;

    // Calculate progress percentage
    const progressPercentage = Math.min((hoursLogged / expectedHours) * 100, 100);

    // Determine status
    const status: TimesheetStatus = currentTimesheet?.status || 'draft';

    // Calculate due date using configurable deadline from organization settings (live or demo mode)
    // deadlineDay is a day-of-week value (0=Sunday, 1=Monday, ..., 5=Friday, 6=Saturday)
    // We need to calculate the offset from the week start to the deadline day
    const deadline = new Date(currentWeek);
    const baseDayOfWeek = deadline.getDay(); // 0=Sunday, 1=Monday, etc.
    const targetDayOfWeek = settings.deadline.deadlineDay;
    const offset = (targetDayOfWeek - baseDayOfWeek + 7) % 7;
    deadline.setDate(deadline.getDate() + offset);
    const [hours, minutes] = settings.deadline.deadlineTime.split(':').map(Number);
    deadline.setHours(hours, minutes, 0, 0);
    const dueDate = deadline;
    
    // Calculate days until deadline
    const diffMs = deadline.getTime() - today.getTime();
    const daysUntilDue = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    // Determine urgency
    const isOverdue = daysUntilDue < 0 && status === 'draft';
    const isDueSoon = daysUntilDue <= 1 && daysUntilDue >= 0 && status === 'draft';

    // PHASE 2: Calculate missing entries (days without time logged)
    const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const workdaysPassed = dayOfWeek === 0 ? 5 : Math.min(dayOfWeek, 5); // Cap at Friday
    
    // Get days with entries
    const daysWithEntries = new Set<string>();
    currentTimesheet?.entries.forEach(entry => {
      Object.entries(entry.hours).forEach(([day, hours]) => {
        if (hours > 0) daysWithEntries.add(day);
      });
    });

    // Calculate expected days (Mon-Fri up to today)
    const expectedDays: string[] = [];
    const dayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    for (let i = 0; i < workdaysPassed; i++) {
      expectedDays.push(dayNames[i]);
    }

    // Find missing days
    const missingDays = expectedDays.filter(day => !daysWithEntries.has(day));
    const hasMissingEntries = missingDays.length > 0 && status === 'draft';

    // Show alert if it's Thursday or Friday and there are missing entries
    const shouldShowMissingAlert = hasMissingEntries && (dayOfWeek === 4 || dayOfWeek === 5);

    return {
      weekStart: currentWeek,
      weekEnd: currentWeekEnd,
      hoursLogged,
      expectedHours,
      progressPercentage,
      status,
      dueDate,
      daysUntilDue,
      isOverdue,
      isDueSoon,
      hasTimesheet: !!currentTimesheet,
      missingDays,
      hasMissingEntries,
      shouldShowMissingAlert,
      // Validation: Only allow adding time when status is Draft or Rejected
      canAddTime: status === 'draft' || status === 'rejected' || status === 'recalled'
    };
  }, [timesheets, currentEmployee, settings, weekStartDay, mode, summary]);

  // Status configuration using accent system
  const statusConfig = {
    draft: {
      label: 'Draft',
      icon: FileText,
      accentClass: 'accent-nile',
      variant: 'secondary' as const
    },
    submitted: {
      label: 'Submitted',
      icon: CheckCircle,
      accentClass: 'accent-info',
      variant: 'default' as const
    },
    approved: {
      label: 'Approved',
      icon: CheckCircle,
      accentClass: 'accent-success',
      variant: 'default' as const
    },
    rejected: {
      label: 'Rejected',
      icon: XCircle,
      accentClass: 'accent-destructive',
      variant: 'destructive' as const
    },
    recalled: {
      label: 'Recalled',
      icon: AlertCircle,
      accentClass: 'accent-warning',
      variant: 'secondary' as const
    },
    locked: {
      label: 'Locked',
      icon: CheckCircle,
      accentClass: 'accent-purple',
      variant: 'default' as const
    }
  };

  const currentStatusConfig = statusConfig[weekData.status];
  const StatusIcon = currentStatusConfig.icon;

  // Progress bar color based on hours logged
  const getProgressColor = () => {
    if (weekData.hoursLogged >= weekData.expectedHours) return 'bg-success-500';
    if (weekData.hoursLogged >= weekData.expectedHours * 0.75) return 'bg-info-500';
    if (weekData.hoursLogged >= weekData.expectedHours * 0.5) return 'bg-warning-400';
    return 'bg-burntorange-500';
  };

  return (
    <Card className="hover:shadow-lg transition-shadow accent-lagoon" data-tour="homepage-summary">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <div className="p-1.5 rounded-md accent-lagoon bg-[var(--accent-bg-outline)]">
                <Clock className="h-4 w-4 text-[var(--accent-text-on-outline)]" />
              </div>
              <span>This Week</span>
            </CardTitle>
            <p className="text-xs text-muted-foreground ml-9">
              {format(weekData.weekStart, 'MMM d')} - {format(weekData.weekEnd, 'MMM d, yyyy')}
            </p>
          </div>
          <Badge 
            variant={currentStatusConfig.variant}
            className="flex items-center gap-1"
            data-tour="homepage-status"
          >
            <StatusIcon className="h-3 w-3" />
            {currentStatusConfig.label}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Hours Logged vs Expected */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Hours Logged</span>
            <span className="text-sm font-bold">
              {weekData.hoursLogged.toFixed(1)} / {weekData.expectedHours}h
            </span>
          </div>
          <Progress 
            value={weekData.progressPercentage} 
            className="h-2"
            indicatorClassName={getProgressColor()}
          />
          <p className="text-xs text-muted-foreground">
            {weekData.hoursLogged >= weekData.expectedHours ? (
              <span className="text-[var(--success-600)] font-medium flex items-center gap-1">
                <CheckCircle className="h-3.5 w-3.5" />
                Target met
              </span>
            ) : (
              <span>
                {(weekData.expectedHours - weekData.hoursLogged).toFixed(1)}h remaining to reach target
              </span>
            )}
          </p>
        </div>

        {/* PHASE 2: Missing Entries Alert */}
        {weekData.shouldShowMissingAlert && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-50 border border-yellow-200">
            <AlertCircle className="h-4 w-4 mt-0.5 text-yellow-600 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-yellow-900">
                Missing Entries Detected
              </p>
              <p className="text-xs text-yellow-700 mt-1">
                You haven't logged time for: {weekData.missingDays.map(day => 
                  day.charAt(0).toUpperCase() + day.slice(1)
                ).join(', ')}
              </p>
            </div>
          </div>
        )}

        {/* Due Date Information */}
        {weekData.status === 'draft' && (
          <div className={`flex items-start gap-2 p-3 rounded-lg ${
            weekData.isOverdue 
              ? 'bg-red-50 border border-red-200' 
              : weekData.isDueSoon 
              ? 'bg-yellow-50 border border-yellow-200'
              : 'bg-blue-50 border border-blue-200'
          }`}>
            <Calendar className={`h-4 w-4 mt-0.5 ${
              weekData.isOverdue 
                ? 'text-red-600' 
                : weekData.isDueSoon 
                ? 'text-yellow-600'
                : 'text-blue-600'
            }`} />
            <div className="flex-1 min-w-0">
              <p className={`text-[12px] ${
                weekData.isOverdue 
                  ? 'text-red-900' 
                  : weekData.isDueSoon 
                  ? 'text-yellow-900'
                  : 'text-blue-900'
              }`}>
                {weekData.isOverdue ? (
                  <>
                    <span className="font-semibold">Overdue</span>
                    <span className="font-normal"> - Submit your timesheet as soon as possible</span>
                  </>
                ) : weekData.isDueSoon ? (
                  <>
                    <span className="font-semibold">Due {weekData.daysUntilDue === 0 ? 'today' : 'tomorrow'}</span>
                    <span className="font-normal"> - Submit your timesheet before end of day</span>
                  </>
                ) : (
                  <>
                    <span className="font-semibold">Due {format(weekData.dueDate, 'EEEE, MMM d')}</span>
                    <span className="font-normal"> - Submit by {format(weekData.dueDate, 'EEEE')} {format(weekData.dueDate, 'h:mm a')}</span>
                  </>
                )}
              </p>
            </div>
            {(weekData.isOverdue || weekData.isDueSoon) && (
              <AlertCircle className={`h-4 w-4 ${
                weekData.isOverdue ? 'text-red-600' : 'text-yellow-600'
              }`} />
            )}
          </div>
        )}

        {/* Submitted/Approved/Rejected Info */}
        {weekData.status === 'submitted' && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200">
            <CheckCircle className="h-4 w-4 mt-0.5 text-blue-600" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-blue-900">Awaiting Approval</p>
              <p className="text-xs text-blue-700">Your timesheet is under review</p>
            </div>
          </div>
        )}

        {weekData.status === 'approved' && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-green-50 border border-green-200">
            <CheckCircle className="h-4 w-4 mt-0.5 text-green-600" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-green-900">Approved</p>
              <p className="text-xs text-green-700">Your timesheet has been approved</p>
            </div>
          </div>
        )}

        {weekData.status === 'rejected' && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
            <XCircle className="h-4 w-4 mt-0.5 text-red-600" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-red-900">Rejected</p>
              <p className="text-xs text-red-700">Please review and resubmit</p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2 pt-2">
          <Button
            variant="default"
            size="sm"
            className="w-full"
            onClick={onNavigateToTimesheet}
            data-tour="homepage-view-timesheet"
          >
            <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
            View Timesheet
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
